import React from 'react';
import InlinePicker from './inline-picker';
import InlineText from './inline-text';
import { branchMode, branchText, newId, PLAYBOOK_CONDITION_OPERATORS } from '../model/playbook-model';

const field = 'w-full min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 focus:border-purple-400 focus:ring-1 focus:ring-purple-300';

export default function BranchCondition({ branch, slotOptions = [], onChange }) {
    const mode = branchMode(branch);
    const rules = branch.expressionList || [];
    const setRule = (index, patch) => onChange({ expressionList: rules.map((rule, i) => i === index ? { ...rule, ...patch } : rule) });
    const addRule = () => onChange({ expressionList: [...rules, { id: newId(), variable: '', operator: 'is', value: '' }] });
    const variables = [...slotOptions.map(option => typeof option === 'string' ? option : option.value), '{sys_input_text}', '{sys_channel}'];
    if (mode === 'else') return <span className="min-w-0 flex-1 py-1 text-[13px] text-gray-500">When no conditions match</span>;
    const modePicker = <InlinePicker
        ariaLabel="Condition mode" tone="plain" searchable={false} align="end"
        value={mode} options={[{ value: 'natural', label: 'Natural language' }, { value: 'expression', label: 'Expression' }]}
        onChange={conditionType => onChange({ conditionType })} />;
    return <div className="min-w-0 flex-1">
        {mode === 'natural' ? <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
            <InlineText
                multiline
                ariaLabel="Branch condition"
                className="flex-1"
                placeholder="the order was already shipped"
                value={branchText(branch)} onChange={value => onChange({ naturalLanguage: value, condition: value })} />
            {modePicker}
            <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 text-[12.5px] text-gray-500">
                <input
                    type="checkbox"
                    aria-label="Conversation history"
                    className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-gray-300"
                    checked={branch.includeConversationHistory !== false}
                    onChange={e => onChange({ includeConversationHistory: e.target.checked })} />
                History
            </label>
        </div> : <>
            <div className="mb-2 flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-xs text-gray-600">Match
                    <select aria-label="Match expressions" className="rounded-md border-0 bg-purple-50 py-1 pl-2 pr-7 text-xs text-purple-700 focus:ring-purple-300" value={branch.operator || 'AND'} onChange={e => onChange({ operator: e.target.value })}>
                        <option value="AND">All expressions</option><option value="OR">Any expression</option>
                    </select>
                </label>
                <button type="button" onClick={addRule} className="rounded-md px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50">+ Add expression</button>
                <span className="ml-auto shrink-0">{modePicker}</span>
            </div>
            <div className="space-y-2">{rules.map((rule, index) => <div key={rule.id || index} className="flex flex-wrap items-center gap-2">
                <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 xl:grid-cols-3">
                    <select aria-label={`Variable ${index + 1}`} className={field} value={rule.variable || ''} onChange={e => setRule(index, { variable: e.target.value })}>
                        <option value="">Choose variable</option>
                        {[...new Set([...variables, ...(rule.variable ? [rule.variable] : [])])].map(name => <option key={name} value={name}>{name}</option>)}
                    </select>
                    <select aria-label={`Operator ${index + 1}`} className={field} value={rule.operator || 'is'} onChange={e => setRule(index, { operator: e.target.value })}>
                        {PLAYBOOK_CONDITION_OPERATORS.map(operator => <option key={operator}>{operator}</option>)}
                    </select>
                    {!['is empty', 'is not empty'].includes(rule.operator) && <input aria-label={`Value ${index + 1}`} className={field} value={rule.value ?? ''} placeholder="Value or {variable}" onChange={e => setRule(index, { value: e.target.value })} />}
                </div>
                <button type="button" aria-label={`Remove expression ${index + 1}`} className="rounded-lg px-2 py-2 text-xs text-gray-500 hover:bg-rose-50 hover:text-rose-600" onClick={() => onChange({ expressionList: rules.filter((_, i) => i !== index) })}>Remove</button>
            </div>)}</div>
        </>}
    </div>;
}
